import React from "react";
import { Lightbulb, Fan, CalendarClock, Camera, Mail, BellRing, RotateCcw } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { DEFAULT_SETTINGS } from "../../lib/constants";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--accent)] transition-colors";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Toggle({ on, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`w-11 h-6 rounded-full relative transition-colors ${on ? "bg-[var(--accent)]" : "bg-[var(--track)]"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function Section({ icon: Icon, title, children, action }) {
  return (
    <Panel className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Icon size={16} className="text-[var(--accent)]" /><Overline>{title}</Overline></div>
        {action}
      </div>
      {children}
    </Panel>
  );
}

function Slider({ label, value, min, max, step = 1, suffix = "", onChange, testid }) {
  return (
    <label className="block">
      <div className="flex justify-between text-xs"><span className="uppercase tracking-wider text-[var(--t2)]">{label}</span><span className="font-mono text-[var(--accent)]">{value}{suffix}</span></div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full mt-2 accent-[var(--accent)]" />
    </label>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[var(--t2)]">{label}</span>
      <input data-testid={testid} type="number" className={`${inputCls} mt-1.5`} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} />
    </label>
  );
}

export default function Personalize() {
  const { settings: s, updateSettings } = useApp();
  const toggleDay = (d) => updateSettings({ ledDays: s.ledDays.includes(d) ? s.ledDays.filter((x) => x !== d) : [...s.ledDays, d] });
  const reset = () => {
    const keys = ["pirHold", "pirBrightness", "fan30", "fan60", "fan100", "fanCritical", "ledSchedule", "ledOn", "ledOff", "ledDays", "camAutoFlash", "camPirSnap", "rapidInterval", "emailReports", "emailTo", "emailFreq", "emailTime", "alertTemp", "alertPower", "alertGas", "alertMqtt"];
    const patch = {}; keys.forEach((k) => (patch[k] = DEFAULT_SETTINGS[k]));
    updateSettings(patch); toast.success("Personalization reset");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <Overline>User Preferences</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Personalize</h2>
      </div>

      <Section icon={Lightbulb} title="PIR Motion Light">
        <Slider label="Hold Time" testid="pz-pir-hold" value={s.pirHold} min={1} max={120} suffix="s" onChange={(v) => updateSettings({ pirHold: v })} />
        <Slider label="Brightness" testid="pz-pir-bright" value={s.pirBrightness} min={0} max={100} suffix="%" onChange={(v) => updateSettings({ pirBrightness: v })} />
      </Section>

      <Section icon={Fan} title="Fan Temperature Thresholds">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <NumField label="30% above (°C)" testid="pz-fan30" value={s.fan30} onChange={(v) => updateSettings({ fan30: v })} />
          <NumField label="60% above (°C)" testid="pz-fan60" value={s.fan60} onChange={(v) => updateSettings({ fan60: v })} />
          <NumField label="100% above (°C)" testid="pz-fan100" value={s.fan100} onChange={(v) => updateSettings({ fan100: v })} />
          <NumField label="Critical (°C)" testid="pz-fancrit" value={s.fanCritical} onChange={(v) => updateSettings({ fanCritical: v })} />
        </div>
      </Section>

      <Section icon={CalendarClock} title="LED Scheduler" action={<Toggle on={s.ledSchedule} testid="pz-led-toggle" onClick={() => updateSettings({ ledSchedule: !s.ledSchedule })} />}>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">On Time</span><input data-testid="pz-led-on" type="time" className={`${inputCls} mt-1.5`} value={s.ledOn} onChange={(e) => updateSettings({ ledOn: e.target.value })} /></label>
          <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">Off Time</span><input data-testid="pz-led-off" type="time" className={`${inputCls} mt-1.5`} value={s.ledOff} onChange={(e) => updateSettings({ ledOff: e.target.value })} /></label>
        </div>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button key={d} data-testid={`pz-day-${d}`} onClick={() => toggleDay(d)} className={`px-3 py-1.5 rounded-full text-xs font-mono transition-colors ${s.ledDays.includes(d) ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)]"}`}>{d}</button>
          ))}
        </div>
      </Section>

      <Section icon={Camera} title="Camera">
        <div className="flex items-center justify-between"><span className="text-sm">Auto-flash on snapshot</span><Toggle on={s.camAutoFlash} testid="pz-autoflash" onClick={() => updateSettings({ camAutoFlash: !s.camAutoFlash })} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">PIR auto-snap</span><Toggle on={s.camPirSnap} testid="pz-pirsnap" onClick={() => updateSettings({ camPirSnap: !s.camPirSnap })} /></div>
        <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">Rapid snap interval</span>
          <select data-testid="pz-rapid" className={`${inputCls} mt-1.5`} value={s.rapidInterval} onChange={(e) => updateSettings({ rapidInterval: parseInt(e.target.value) })}>
            {[3, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v < 60 ? `${v}s` : "1 min"}</option>)}
          </select>
        </label>
      </Section>

      <Section icon={Mail} title="Email Reports" action={<Toggle on={s.emailReports} testid="pz-email-toggle" onClick={() => updateSettings({ emailReports: !s.emailReports })} />}>
        <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">Recipient</span><input data-testid="pz-email-to" className={`${inputCls} mt-1.5`} value={s.emailTo} onChange={(e) => updateSettings({ emailTo: e.target.value })} /></label>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">Frequency</span>
            <select data-testid="pz-email-freq" className={`${inputCls} mt-1.5`} value={s.emailFreq} onChange={(e) => updateSettings({ emailFreq: e.target.value })}>
              <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
            </select>
          </label>
          <label className="block"><span className="text-xs uppercase tracking-wider text-[var(--t2)]">Send Time</span><input data-testid="pz-email-time" type="time" className={`${inputCls} mt-1.5`} value={s.emailTime} onChange={(e) => updateSettings({ emailTime: e.target.value })} /></label>
        </div>
      </Section>

      <Section icon={BellRing} title="Alerts">
        <div className="flex items-center justify-between"><span className="text-sm">Temperature alert</span><Toggle on={s.alertTemp} testid="pz-alert-temp" onClick={() => updateSettings({ alertTemp: !s.alertTemp })} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">Power alert</span><Toggle on={s.alertPower} testid="pz-alert-power" onClick={() => updateSettings({ alertPower: !s.alertPower })} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">Air quality alert (MQ-135 DANGER)</span><Toggle on={s.alertGas} testid="pz-alert-gas" onClick={() => updateSettings({ alertGas: !s.alertGas })} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">MQTT disconnect alert</span><Toggle on={s.alertMqtt} testid="pz-alert-mqtt" onClick={() => updateSettings({ alertMqtt: !s.alertMqtt })} /></div>
      </Section>

      <button data-testid="pz-reset" onClick={reset} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--bd)] text-sm hover:border-[var(--bd-strong)] transition-colors">
        <RotateCcw size={15} /> Reset Personalize
      </button>
    </div>
  );
}
